import React from 'react';
import { ApiSanctionHit } from '../api';
import ReactHtmlParser from 'react-html-parser';
import RmsPopover from '../common/popover/RmsPopover';
import { isHitFiltered } from './alert/matches/helper';
import { Popover, Table } from 'react-bootstrap';
import { parseJsonSilent } from '../common/helper';

const matchDescription = (originalInput: string | undefined, hit: ApiSanctionHit): string => {
  const jsonData = parseJsonSilent(hit.matchData);
  const matchResult = hit.highlight ? hit.highlight : jsonData['properties']?.['name'];
  const score = hit.score !== 0 ? `${hit.score}%` : ``;
  const input = originalInput ? originalInput : hit.inputData;
  return `${score} ${input} ⟷ ${matchResult}`;
};

const getUnfilteredHits = (hits: ApiSanctionHit[]) => {
  return hits.filter((hit) => !isHitFiltered(hit));
};

const filterHits = (hits: ApiSanctionHit[], status?: string | 'ALL' | 'ACTIVE') => {
  switch (status) {
    case 'ALL':
    case 'FILTERED':
    case undefined:
    case null:
      return hits;
    default:
      return getUnfilteredHits(hits);
  }
};

const sanctionMatchesBody = (originalInput: string | undefined, hits: ApiSanctionHit[]) => {
  return hits.map((hit) => {
    return (
      <tr key={hit.id}>
        <td className='alert-details'>{ReactHtmlParser(matchDescription(originalInput, hit))}</td>
      </tr>
    );
  });
};

const moreHitsCount = (hits: ApiSanctionHit[]) => {
  const filteredHits = hits.filter((hit) => isHitFiltered(hit));
  let text = '';
  if (hits.length > filteredHits.length) {
    text += `${hits.length - filteredHits.length} more unfiltered matches`;
  }
  if (hits.length > filteredHits.length && filteredHits.length > 0) {
    text += ', ';
  }
  if (filteredHits.length > 0) {
    text += `${filteredHits.length} more filtered matches`;
  }
  return text;
};

function screeningMatchesOverlay(originalInput: string | undefined, hits: ApiSanctionHit[]) {
  return (
    <Popover id='matches-popover' className='sanction-match-popover'>
      <Popover.Body>
        <Table title='Matches' className='table-fixed matches' responsive={true} striped={true} hover={false} size='sm'>
          {/* Limit matches to 5 elements */}
          <tbody>{sanctionMatchesBody(originalInput, hits.slice(0, 5))}</tbody>
          {hits.length > 5 && (
            <tfoot className='more-hits'>
              <tr>
                <td>{moreHitsCount(hits.slice(5))}</td>
              </tr>
            </tfoot>
          )}
        </Table>
      </Popover.Body>
    </Popover>
  );
}

interface ScreeningMatchesPopoverProps {
  originalInput: string | undefined;
  hits: ApiSanctionHit[];
  status?: string;
}

export const ScreeningMatchesPopover = ({ originalInput, hits, status }: ScreeningMatchesPopoverProps) => {
  const shownHits = filterHits(hits, status);
  if (shownHits.length === 0) return <span className='property'>matches loading...</span>;

  // Set first uncleared hit as match if it doesn't exist then the first hit
  const unfilteredHits = getUnfilteredHits(shownHits);
  const match = unfilteredHits.length > 0 ? unfilteredHits[0] : shownHits[0];

  if (shownHits.length > 1) {
    return (
      <RmsPopover overlay={screeningMatchesOverlay(originalInput, shownHits)}>
        <span className='hover-link'>
          {ReactHtmlParser(matchDescription(originalInput, match))} • {shownHits.length - 1} more
        </span>
      </RmsPopover>
    );
  } else {
    return <span>{ReactHtmlParser(matchDescription(originalInput, match))}</span>;
  }
};

export default ScreeningMatchesPopover;
